<template>
  <div class="overflow-auto">
    <ve-table
      max-height="calc(100vh - 15.500rem)"
      scroll-width="50rem"
      row-key-field-name="id"
      :columns="columns"
      :table-data="data"
      :border-around="false"
      :row-style-option="getRowStyleOption()"
      :expand-option="expandOption"
    />
  </div>
</template>

<script>
import { VeTable } from 'vue-easytable';
import Message from './Message.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    Message,
    VeTable,
  },
  data() {
    return {
      selectedRowKeys: [],
      expandOption: {
        render: ({ row, column, rowIndex }, h) => {
          return h(Message, {
            props: {
              headerImage: this.getHeaderImage(row),
              contentHeader: this.getContentHeader(row),
              contentMessages: this.getContentMessages(row),
              buttonMessages: this.getButtonMessages(row),
              isButtonMessages: this.getIsButtonMessages(row),
            },
          });
        },
      },
    };
  },
  props: {
    data: {
      type: Array,
      default: [],
    },
    meta: {
      type: Object,
      default: {},
    },
  },
  methods: {
    getRowStyleOption() {
      return {
        clickHighlight: false,
        stripe: false,
      };
    },
    getHeaderImage(row) {
      const header = row?.template_data?.components?.find(
        c => c.type === 'HEADER' && c.format === 'IMAGE'
      );
      return header?.example?.header_handle?.[0] || null;
    },
    getContentMessages(row) {
      const body = row?.params?.message?.content;

      return body || '';
    },
    getButtonMessages(row) {
      const buttons = row?.template_data?.components?.find(
        c => c.type === 'BUTTONS'
      );
      return Array.isArray(buttons?.buttons) ? buttons.buttons : [];
    },
    getIsButtonMessages(row) {
      return this.getButtonMessages(row).length > 0;
    },
    getContentHeader(row) {
      const components = row?.template_data?.components || [];

      const headerComponent = components.find(
        component => component.type === 'HEADER' && component.format === 'TEXT'
      );

      return headerComponent?.text || null;
    },
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    columns() {
      return [
        {
          field: 'index',
          key: 'index',
          title: 'No',
          width: 10,
          align: 'center',
          fixed: 'left',
          renderBodyCell: ({ rowIndex }) => {
            return (
              (this.meta.currentPage - 1) * this.meta.perPage + (rowIndex + 1)
            );
          },
        },
        {
          field: '',
          key: 'a',
          type: 'expand',
          title: '',
          width: 10,
          align: 'center',
        },
        {
          field: 'name',
          key: 'name',
          title: this.$t('BROADCAST.TABLE.NAME'),
          fixed: 'left',
          width: 25,
          align: 'left',
          renderBodyCell: ({ row }) => {
            return (
              <div class="row-user">
                <div class="user-block">
                  <div>{row.contact.name}</div>
                  <p class="user-handle">
                    {this.$t('BROADCAST.TABLE.APPROVED_BY')} : {row.approved_by?.name || this.$t('BROADCAST.TABLE.SYSTEM_USER')}
                  </p>
                </div>
              </div>
            );
          },
        },
        {
          field: 'phone_number',
          key: 'phone_number',
          title: this.$t('BROADCAST.TABLE.PHONE_NUMBER'),
          width: 25,
          align: 'left',
          renderBodyCell: ({ row }) => {
            return (
              <div class="row-user">
                <div class="user-block">{row.phone_number}</div>
              </div>
            );
          },
        },
        {
          field: 'created_at',
          key: 'created_at',
          title: this.$t('BROADCAST.TABLE.CREATED_DATE'),
          width: 25,
          align: 'center',
          renderBodyCell: ({ row }) => {
            const date = new Date(row.created_at);
            const formattedDate = new Intl.DateTimeFormat('id-ID', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }).format(date);

            const formattedTime = new Intl.DateTimeFormat('id-ID', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }).format(date);
            return (
              <div class="row-date">
                <div class="date-block">
                  <div>{formattedDate}</div>
                  <div>{formattedTime}</div>
                </div>
              </div>
            );
          },
        },
        {
          field: 'status',
          key: 'status',
          title: this.$t('BROADCAST.TABLE.STATUS'),
          width: 25,
          align: 'center',
          renderBodyCell: ({ row }) => {
            let icon;
            const status = row.message?.status;
            // const status = 'failed';
            const errorMsg =
              row.message?.content_attributes?.external_error ||
              'Pesan Gagal Terkirim, error : unknown';

            switch (status) {
              case 'sent':
                icon = (
                  <div class="tags">
                    <span class="sent-tag">
                      {this.$t('BROADCAST.STATUS.SENT')}
                    </span>
                  </div>
                );
                break;
              case 'delivered':
                icon = (
                  <div class="tags">
                    <span class="finish-tag">
                      {this.$t('BROADCAST.STATUS.DELIVERED')}
                    </span>
                  </div>
                );
                break;
              case 'read':
                icon = (
                  <div class="tags">
                    <span class="read-tag">
                      {this.$t('BROADCAST.STATUS.READ')}
                    </span>
                  </div>
                );
                break;
              case 'failed':
                icon = (
                  <div class="tags">
                    <div class="tooltip-container">
                      <span class="failed-tag">
                        {this.$t('BROADCAST.STATUS.FAILED')}
                      </span>
                      <div class="tooltip-text">{errorMsg}</div>
                    </div>
                  </div>
                );
                break;
              default:
                icon = (
                  <div class="tags">
                    <span class="pending-tag">
                      {this.$t('BROADCAST.STATUS.SENDING')}
                    </span>
                  </div>
                );
            }
            return (
              <div
                class="row-user"
                style="display: flex; justify-content: center; align-items: center; height: 100%;"
              >
                <div class="user-block">
                  <span>{icon}</span>
                </div>
              </div>
            );
          },
        },
        {
          field: 'approved_at',
          key: 'approved_at',
          title: this.$t('BROADCAST.TABLE.APPROVED_DATE'),
          width: 25,
          align: 'center',
          renderBodyCell: ({ row }) => {
            if (!row.approved_at) {
              return (
                <div class="row-date">
                  <div class="date-block">
                    {this.$t('BROADCAST.NOT_APPROVED')}
                  </div>
                </div>
              );
            }

            const date = new Date(row.approved_at);

            const formattedDate = new Intl.DateTimeFormat('id-ID', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }).format(date);

            const formattedTime = new Intl.DateTimeFormat('id-ID', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }).format(date);

            return (
              <div class="row-date">
                <div class="date-block">
                  <div>{formattedDate}</div>
                  <div>{formattedTime}</div>
                </div>
              </div>
            );
          },
        },
        {
          field: 'conversation_id',
          key: 'conversation_id',
          title: 'Conv ID',
          width: 15,
          fixed: 'center',
          renderBodyCell: ({ row }) => (
            <div class="justify-center flex gap-4">
              <router-link
                to={`/app/accounts/${this.accountId}/conversations/${row.conversation_id}`}
              >
                #{row.conversation_id}
              </router-link>
            </div>
          ),
        },
      ];
    },
  },
};
</script>

<style scoped>
.row-user {
  @apply items-center flex text-left my-4;
}

.row-date {
  @apply items-center flex text-center my-4 justify-center;
}

.pending-tag {
  @apply bg-yellow-600 text-white text-xs py-0.5 px-3 rounded-md;
}

.failed-tag {
  @apply bg-red-600 text-white text-xs py-0.5 px-3 rounded-md;
}

.finish-tag {
  background-color: #ffa500;
  color: white;
  font-size: 0.75rem;
  line-height: 1rem;
  padding-top: 0.125rem;
  padding-bottom: 0.125rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  border-radius: 0.375rem;
}

.sent-tag {
  background-color: #16a34a;
  color: white;
  font-size: 0.75rem;
  line-height: 1rem;
  padding-top: 0.125rem;
  padding-bottom: 0.125rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  border-radius: 0.375rem;
}

.read-tag {
  background-color: #1f93ff;
  color: white;
  font-size: 0.75rem;
  line-height: 1rem;
  padding-top: 0.125rem;
  padding-bottom: 0.125rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  border-radius: 0.375rem;
}

::v-deep .ve-table-expand-td-content {
  padding: 0 !important;
}

::v-deep(.ve-table-expand-tr > .ve-table-expand-td) {
  background-color: #aeaeae !important;
  padding: 1rem !important;
}

.tooltip-container {
  position: relative;
  display: inline-block;
}

.tooltip-text {
  visibility: hidden;
  background-color: rgba(0, 0, 0, 0.8);
  color: #fff;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
}

.tooltip-container:hover .tooltip-text {
  visibility: visible;
}

.user-handle {
  @apply text-slate-400 text-xs py-1;
}
</style>
